import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DISPLAY_NAME_MAX, findSkin, homePath, localeOfHomePath, SUPPORTED_LOCALES, type Locale } from '@snakefeast/shared';
import { ArenaPreview } from '../components/ArenaPreview';
import { AuthModal } from '../components/AuthForm';
import { GetApp } from '../components/GetApp';
import { HeroClip } from '../components/HeroClip';
import { SkinPreview } from '../components/SkinPreview';
import { CHEVRON, MODE_LOOK, SEO_FAQ, SEO_MODES, STEPS, TRUST } from '../content/landing';
import { LanguageLinks } from '../content/LanguageLinks';
import { Picture } from '../content/Picture';
import { Modal } from '../components/ui';
import { LOCALE_NAMES, setLocale } from '../i18n';
import { api, authApi } from '../lib/api';
import { requestAutoPlay } from '../lib/autoplay';
import { isNative } from '../lib/platform';
import { instantMultiplayer, invitedRoom, PORTAL, portalUsername, portalUserToken, siteUrl } from '../lib/portal';
import { unlockAudio } from '../lib/sound';
import { useSession } from '../store/session';
import { toast } from '../store/toasts';
import { GameIcon, Glyph } from '../components/GameIcon';

const FEATURES = [
  { id: 'realtime', icon: 'M13 2 3 14h9l-1 8 10-12h-9l1-8z' },
  { id: 'voice', icon: 'M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM19 10v2a7 7 0 0 1-14 0v-2M12 19v3' },
  { id: 'ranked', icon: 'M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0zM7 6H4a3 3 0 0 0 3 4M17 6h3a3 3 0 0 1-3 4' },
  { id: 'platforms', icon: 'M5 2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zM8 22h8M12 18v4' },
] as const;

/** Shop skins shown on the landing, drawn still (no animation loop on the first page people see). */
const SHOWCASE_SKINS = ['neon-cyber', 'molten', 'russell-viper', 'aurora', 'gold-royal', 'pit-viper'] as const;
const skinLabel = (id: string) => id.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const LANDING_SHOTS = ['bigArena', 'royaleZone'] as const;

const Svg =({ d, size = 20 }: { d: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);

let portalAutoStarted = false;
/** Opened from a room invite link: skip the landing, so the friend lands in the room in one tap. */
const fromInvite = () => /^\/join\/[A-Za-z0-9]{6}$/.test(window.location.pathname);

export function Welcome() {
  const { t, i18n } = useTranslation();
  const signedIn = useSession((s) => s.signedIn);
  const [auth, setAuth] = useState<null | 'login' | 'register'>(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const nav = useNavigate();
  // On the landing ("/", "/hi", "/es"…) a new language also moves to that language's URL.
  const onLanding = window.location.pathname === '/' || !!localeOfHomePath(window.location.pathname);
  const changeLanguage = (l: Locale) => {
    void setLocale(l);
    if (onLanding && !PORTAL) nav(homePath(l) + window.location.search, { replace: true });
  };
  const challengeId = params.get('challenge');
  const [challenger, setChallenger] = useState<{ name: string; score: number } | null>(null);

  // The tab title follows the language (the prerendered page set it for the language it was opened in).
  useEffect(() => {
    if (!PORTAL) document.title = t('welcome.meta.title');
  }, [t, i18n.language]);

  // Portal players already chose the game on the portal's own page, and invited friends already chose
  // to join: skip our landing and start as a guest (someone signed in on this device is already authed).
  useEffect(() => {
    if ((PORTAL || fromInvite()) && !portalAutoStarted) {
      portalAutoStarted = true; // StrictMode runs effects twice in dev; create one guest only
      void guest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opened from a "beat my score" link: say who is waiting (the URL survives sign-in, so the Solo screen picks it up next).
  useEffect(() => {
    if (!challengeId) return;
    void api<{ name: string; score: number }>(`/api/solo/runs/${challengeId}/public`, { auth: false }).then(setChallenger).catch(() => {});
  }, [challengeId]);

  const guest = async () => {
    unlockAudio();
    setBusy(true);
    try {
      // On CrazyGames, a player signed in there starts as their CrazyGames account (same progress on every device).
      const cgToken = PORTAL === 'crazygames' ? await portalUserToken() : null;
      const session = cgToken ? await authApi.crazygames(cgToken, i18n.language).catch(() => authApi.guest(i18n.language)) : await authApi.guest(i18n.language);
      // "Play now" means play now: go straight into a game, not to the lobby. Links to a room or a
      // "beat my score" challenge keep their own destination.
      // (Portals frame us at their own URL and always start fresh; a portal invite goes to its room instead.)
      if ((PORTAL || onLanding) && !challengeId && !invitedRoom() && !instantMultiplayer()) requestAutoPlay();
      signedIn(session);
      // Portals: play under the portal account's name, so friends recognize each other (a linked account already has it).
      if (PORTAL && session.isGuest) {
        const name = await portalUsername();
        if (name) void useSession.getState().updateMe({ displayName: name.slice(0, DISPLAY_NAME_MAX) }).catch(() => {});
      }
    } catch {
      toast(t('errors.network'), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (PORTAL || fromInvite()) {
    return (
      <div className="welcome">
        {busy ? <span className="spin" /> : <button className="cta-primary" onClick={guest}>{t('welcome.playGuest')}</button>}
      </div>
    );
  }

  // The apps open on a game's title screen, not the website's landing page: its FAQ, footer, language
  // links and browser-window mockup read as "a website in a wrapper" (App Store guideline 4.2).
  if (isNative) {
    return (
      <div className="app-start">
        <header className="app-start-top">
          <label className="lang-pill">
            <span className="visually-hidden">{t('settings.language')}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" />
            </svg>
            <select value={i18n.language} onChange={(e) => changeLanguage(e.target.value as Locale)}>
              {SUPPORTED_LOCALES.map((l) => (
                <option key={l} value={l}>{LOCALE_NAMES[l]}</option>
              ))}
            </select>
          </label>
        </header>

        <div className="app-start-body">
          <div className="app-start-brand reveal" style={{ ['--i' as string]: 0 }}>
            <img src={`${import.meta.env.BASE_URL}icon.svg`} alt="" className="app-start-logo" width={84} height={84} />
            <h1>{t('app.name')}</h1>
            <p>{t('welcome.title')}</p>
          </div>

          <div className="app-start-stage reveal" style={{ ['--i' as string]: 1 }} aria-hidden="true">
            <ArenaPreview />
          </div>

          <div className="app-start-actions reveal" style={{ ['--i' as string]: 2 }}>
            {challenger && (
              <div className="card challenge-card">
                <GameIcon name="target" size={34} className="challenge-icon" />
                <div className="grow">
                  <strong>{t('solo.challenge.title', { name: challenger.name })}</strong>
                  <div className="muted">{t('solo.challenge.welcome', { score: challenger.score.toLocaleString() })}</div>
                </div>
              </div>
            )}
            <button className="cta-primary" onClick={guest} disabled={busy}>
              <span className="cta-shine" aria-hidden="true" />
              {busy ? (
                <span className="spin" />
              ) : (
                <>
                  <span className="cta-play" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24"><path d="M7 4.5v15l12-7.5z" fill="currentColor" /></svg>
                  </span>
                  {t('home.playNow')}
                </>
              )}
            </button>
            <div className="cta-secondary">
              <button className="btn-glass" onClick={() => setAuth('login')}>{t('welcome.signIn')}</button>
              <button className="btn-glass accent" onClick={() => setAuth('register')}>{t('welcome.createAccount')}</button>
            </div>
            <nav className="app-start-legal">
              <Link to="/privacy">{t('settings.privacy')}</Link>
              <span aria-hidden="true">·</span>
              <Link to="/terms">{t('settings.terms')}</Link>
            </nav>
          </div>
        </div>

        {auth && <AuthModal initialMode={auth} onClose={() => setAuth(null)} />}
      </div>
    );
  }

  return (
    <div className="landing">
      <div className="landing-grid-bg" aria-hidden="true" />

      <header className="landing-top">
        <div className="landing-logo">
          <img src={`${import.meta.env.BASE_URL}icon.svg`} alt="" width={36} height={36} />
          <span>{t('app.name')}</span>
        </div>
        <label className="lang-pill">
          <span className="visually-hidden">{t('settings.language')}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" />
          </svg>
          <select value={i18n.language} onChange={(e) => changeLanguage(e.target.value as Locale)}>
            {SUPPORTED_LOCALES.map((l) => (
              <option key={l} value={l}>{LOCALE_NAMES[l]}</option>
            ))}
          </select>
        </label>
      </header>

      <main className="landing-hero">
        <section className="landing-copy">
          {/* The pill carries the searchable heading; the big slogan is styling, not the page's topic. */}
          <h1 className="eyebrow reveal" style={{ ['--i' as string]: 0 }}>
            <span className="live-dot" />
            {t('welcome.h1')}
          </h1>
          <p className="landing-title shine reveal" style={{ ['--i' as string]: 1 }}>{t('welcome.title')}</p>
          <p className="landing-sub reveal" style={{ ['--i' as string]: 2 }}>{t('welcome.subtitle')}</p>
          <ul className="trust-chips reveal" style={{ ['--i' as string]: 2 }}>
            {TRUST.map((c) => (
              <li key={c.id}><Glyph e={c.icon} />{t(`welcome.trust.${c.id}`)}</li>
            ))}
          </ul>

          {challenger && (
            <div className="card challenge-card reveal" style={{ ['--i' as string]: 3 }}>
              <GameIcon name="target" size={34} className="challenge-icon" />
              <div className="grow">
                <strong>{t('solo.challenge.title', { name: challenger.name })}</strong>
                <div className="muted">{t('solo.challenge.welcome', { score: challenger.score.toLocaleString() })}</div>
              </div>
            </div>
          )}

          <div className="landing-ctas reveal" style={{ ['--i' as string]: 3 }}>
            <button className="cta-primary" onClick={guest} disabled={busy}>
              <span className="cta-shine" aria-hidden="true" />
              {busy ? (
                <span className="spin" />
              ) : (
                <>
                  <span className="cta-play" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24"><path d="M7 4.5v15l12-7.5z" fill="currentColor" /></svg>
                  </span>
                  {t('welcome.playGuest')}
                </>
              )}
            </button>
            <div className="cta-secondary">
              <button className="btn-glass" onClick={() => setAuth('login')}>{t('welcome.signIn')}</button>
              <button className="btn-glass accent" onClick={() => setAuth('register')}>{t('welcome.createAccount')}</button>
            </div>
          </div>

          <p className="landing-note reveal" style={{ ['--i' as string]: 4 }}>
            <Svg d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" size={16} />
            {t('welcome.guestNote')}
          </p>
          <div className="reveal" style={{ ['--i' as string]: 5, marginTop: 14 }}>
            <GetApp />
          </div>

          <ul className="feature-grid reveal" style={{ ['--i' as string]: 5 }}>
            {FEATURES.map((f) => (
              <li key={f.id} className="feature">
                <span className="feature-icon"><Svg d={f.icon} /></span>
                <span>
                  <strong>{t(`welcome.features.${f.id}.title`)}</strong>
                  <small>{t(`welcome.features.${f.id}.desc`)}</small>
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="landing-visual reveal" style={{ ['--i' as string]: 2 }} aria-label={t('welcome.liveDemo')}>
          <div className="preview-glow" aria-hidden="true" />
          <div className="preview-frame">
            <div className="preview-bar">
              <span className="dots" aria-hidden="true"><i /><i /><i /></span>
              <span className="preview-label"><span className="live-dot" /> {t('welcome.liveDemo')}</span>
            </div>
            <div className="preview-stage">
              <HeroClip />
            </div>
          </div>
        </section>
      </main>

      <section className="landing-section landing-steps" aria-labelledby="landing-steps-title">
        <h2 id="landing-steps-title">{t('welcome.steps.title')}</h2>
        <ol>
          {STEPS.map((s, i) => (
            <li key={s}>
              <span className="step-num" aria-hidden="true">{i + 1}</span>
              <strong>{t(`welcome.steps.${s}.title`)}</strong>
              <span>{t(`welcome.steps.${s}.desc`)}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* Crawlable description of the game below the fold: the landing is the only page search engines see of the app. */}
      <section className="landing-info" aria-labelledby="landing-info-title">
        <h2 id="landing-info-title">{t('welcome.seo.title')}</h2>
        <p className="landing-sub">{t('welcome.seo.intro')}</p>
        <h3>{t('welcome.seo.modesTitle')}</h3>
        <ul className="landing-modes">
          {SEO_MODES.map((m) => (
            <li key={m} style={{ ['--mode' as string]: MODE_LOOK[m].color }}>
              <span className="mode-icon"><Glyph e={MODE_LOOK[m].icon} size={30} /></span>
              <strong>{t(`welcome.seo.modes.${m}.title`)}</strong>
              <span>{t(`welcome.seo.modes.${m}.desc`)}</span>
            </li>
          ))}
        </ul>
        <div className="landing-skins">
          <h3>{t('welcome.skins.title')}</h3>
          <p className="landing-sub">{t('welcome.skins.sub')}</p>
          <ul>
            {SHOWCASE_SKINS.map((id) => (
              <li key={id}>
                <SkinPreview skin={findSkin(id)} size={92} animate={false} />
                <span>{skinLabel(id)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="landing-shots">
          {LANDING_SHOTS.map((id) => (
            <Picture key={id} id={id} alt={t(`welcome.seo.images.${id}.alt`)} caption={t(`welcome.seo.images.${id}.caption`)} />
          ))}
        </div>
        <h3>{t('welcome.seo.faqTitle')}</h3>
        <div className="landing-faq">
          {SEO_FAQ.map((q) => (
            <details key={q}>
              <summary>
                {t(`welcome.seo.faq.${q}.q`)}
                <svg className="faq-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={CHEVRON} /></svg>
              </summary>
              <p>{t(`welcome.seo.faq.${q}.a`)}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="landing-final" aria-labelledby="landing-final-title">
        <h2 id="landing-final-title">{t('welcome.finalCta.title')}</h2>
        <p>{t('welcome.finalCta.sub')}</p>
        <button className="cta-primary" onClick={guest} disabled={busy}>
          <span className="cta-shine" aria-hidden="true" />
          {busy ? <span className="spin" /> : t('welcome.playGuest')}
        </button>
      </section>

      <footer className="landing-foot">
        {/* Store apps get named here once they're live; until then a link nobody can find costs trust. */}
        <span>{t('welcome.features.platforms.desc')}</span>
        <span className="sep" aria-hidden="true" />
        <Link to="/how-to-play">{t('welcome.howToPlay')}</Link>
        <Link to="/guides">{t('welcome.guides')}</Link>
        <Link to="/multiplayer-snake-game">{t('welcome.playWithFriends')}</Link>
        <Link to="/about">{t('welcome.about')}</Link>
        <Link to="/contact">{t('welcome.contact')}</Link>
        <Link to="/privacy">{t('settings.privacy')}</Link>
        <Link to="/terms">{t('settings.terms')}</Link>
      </footer>
      <LanguageLinks label={t('welcome.languages')} current={i18n.language as Locale} onPick={changeLanguage} />

      {auth && (
        <AuthModal initialMode={auth} onClose={() => setAuth(null)} />
      )}
    </div>
  );
}
